/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Write or edit a recipe — and the review screen for everything the AI produces.
 *
 * The web had no recipe editor at all. You could generate, scan or import one,
 * but the review step was read-only: a wrong quantity or a misread ingredient
 * had to be saved first and then fixed... except nothing could edit it
 * afterwards either. Writing one by hand was impossible entirely.
 *
 * ── WHY ONE COMPONENT FOR ALL FOUR CASES ────────────────────────────────────
 * Blank create, edit-existing, and reviewing an AI / OCR / URL draft are the
 * same form over the same shape. Splitting them produces three near-identical
 * files that drift, and the review path is precisely the one that most needs
 * every field to be editable.
 *
 * Nothing reaches the library until Save is pressed. That review step is the
 * whole safety story for generated content: a model can produce a plausible
 * recipe with a wrong quantity or an ingredient somebody is allergic to, and a
 * person reading it is the only reliable check.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { RecipesApi } from "../../api/endpoints.ts";
import type { Recipe, RecipeDraft } from "../../api/types.ts";
import { Banner, ErrorBox, Loading, PageHeader } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import { RecipePhotoField } from "./RecipePhotoField.tsx";

type ScaleMode = "linear" | "fixed" | "sublinear";

interface EditIngredient {
  key: string;
  displayName: string;
  quantity: string;
  unit: string;
  prepNote: string;
  isOptional: boolean;
  scaleMode: ScaleMode;
}

interface EditStep {
  key: string;
  text: string;
  durationMinutes: string;
}

interface Form {
  title: string;
  description: string;
  servingsBase: string;
  prepMinutes: string;
  cookMinutes: string;
  cuisine: string;
  course: string;
  dietTags: string;
  notes: string;
  source: string;
  sourceName: string;
  ingredients: EditIngredient[];
  steps: EditStep[];
}

/** Stable row keys. Deriving them from content re-mounts the input on every
 *  keystroke, which loses focus after each character. */
let seq = 0;
const nextKey = () => `row-${seq++}`;

const blankIngredient = (): EditIngredient => ({
  key: nextKey(), displayName: "", quantity: "", unit: "",
  prepNote: "", isOptional: false, scaleMode: "linear",
});
const blankStep = (): EditStep => ({ key: nextKey(), text: "", durationMinutes: "" });

function emptyForm(): Form {
  return {
    title: "", description: "", servingsBase: "4", prepMinutes: "", cookMinutes: "",
    cuisine: "", course: "", dietTags: "", notes: "",
    source: "manual", sourceName: "",
    ingredients: [blankIngredient()], steps: [blankStep()],
  };
}

function fromDraft(d: RecipeDraft): Form {
  return {
    title: d.title ?? "",
    description: d.description ?? "",
    servingsBase: String(d.servingsBase ?? 4),
    prepMinutes: d.prepMinutes == null ? "" : String(d.prepMinutes),
    cookMinutes: d.cookMinutes == null ? "" : String(d.cookMinutes),
    cuisine: d.cuisine ?? "",
    course: d.course ?? "",
    dietTags: (d.dietTags ?? []).join(", "),
    notes: d.notes ?? "",
    source: "manual",
    sourceName: "",
    ingredients: (d.ingredients ?? []).map((i) => ({
      key: nextKey(),
      displayName: i.displayName ?? "",
      quantity: i.quantity == null ? "" : String(i.quantity),
      unit: i.unit ?? "",
      prepNote: i.prepNote ?? "",
      isOptional: !!i.isOptional,
      scaleMode: (i.scaleMode ?? "linear") as ScaleMode,
    })),
    steps: (d.steps ?? []).map((s) => ({
      key: nextKey(),
      text: s.text ?? "",
      durationMinutes: s.durationMinutes == null ? "" : String(s.durationMinutes),
    })),
  };
}

function fromRecipe(r: Recipe): Form {
  return {
    title: r.title,
    description: r.description ?? "",
    servingsBase: String(r.servings_base),
    // prep_minutes and cook_minutes separately, NOT total_minutes: total is a
    // generated column, and writing it back into the cook field would double
    // the prep time on every save.
    prepMinutes: r.prep_minutes == null ? "" : String(r.prep_minutes),
    cookMinutes: r.cook_minutes == null ? "" : String(r.cook_minutes),
    cuisine: r.cuisine ?? "",
    course: r.course ?? "",
    dietTags: (r.diet_tags ?? []).join(", "),
    notes: r.notes ?? "",
    source: r.source ?? "manual",
    sourceName: r.source_name ?? "",
    ingredients: r.ingredients.map((i) => ({
      key: nextKey(),
      displayName: i.display_name,
      quantity: i.quantity == null ? "" : String(i.quantity),
      unit: i.unit ?? "",
      prepNote: i.prep_note ?? "",
      isOptional: i.is_optional,
      scaleMode: (i.scale_mode ?? "linear") as ScaleMode,
    })),
    steps: r.steps.map((s) => ({
      key: nextKey(),
      text: s.text,
      durationMinutes: s.duration_minutes == null ? "" : String(s.duration_minutes),
    })),
  };
}

const num = (s: string): number | undefined => {
  const n = Number(s.replace(",", "."));
  return s.trim() === "" || Number.isNaN(n) ? undefined : n;
};

function toBody(f: Form, _source: string | undefined, _sourceUrl?: string) {
  // Use form's source/sourceName if present, otherwise fall back to nav state
  const source = f.source || _source || "manual";
  const sourceName = f.sourceName || _sourceUrl || undefined;
  return {
    title: f.title.trim(),
    description: f.description.trim() || undefined,
    servingsBase: num(f.servingsBase) ?? 4,
    prepMinutes: num(f.prepMinutes),
    cookMinutes: num(f.cookMinutes),
    cuisine: f.cuisine.trim() || undefined,
    course: f.course.trim() || undefined,
    dietTags: f.dietTags.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
    notes: f.notes.trim() || undefined,
    source,
    sourceName: sourceName || undefined,
    sourceUrl: _sourceUrl,
    ingredients: f.ingredients
      .filter((i) => i.displayName.trim())
      .map((i) => ({
        displayName: i.displayName.trim(),
        quantity: num(i.quantity),
        unit: i.unit.trim() || undefined,
        prepNote: i.prepNote.trim() || undefined,
        isOptional: i.isOptional,
        scaleMode: i.scaleMode,
      })),
    steps: f.steps
      .filter((s) => s.text.trim())
      .map((s) => ({ text: s.text.trim(), durationMinutes: num(s.durationMinutes) })),
  };
}

/** Router state pushed by the AI flows when they hand a draft over. */
interface EditorNavState {
  draft?: RecipeDraft;
  source?: string;
  sourceUrl?: string;
  lowConfidence?: string[];
  /**
   * A page photo carried over from the scan flow.
   *
   * Migration 010 deliberately does NOT retain scanned images server-side —
   * only the OCR markdown — so if the user wants the cookbook page as the
   * recipe's picture, the bytes have to travel with the draft through the
   * client. This is that.
   */
  scanPhoto?: string;
  /** A picture found on the imported page. Fetched and stored by the server. */
  imageUrl?: string;
}

export function RecipeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const nav = (location.state ?? {}) as EditorNavState;
  const isEdit = Boolean(id);

  const [form, setForm] = useState<Form>(() =>
    nav.draft ? fromDraft(nav.draft) : emptyForm());
  // A picked (or scan-carried) photo waiting to be uploaded. On a NEW recipe
  // there is no id to attach it to until the create call returns, so it is held
  // here and uploaded immediately afterwards.
  const [pendingPhoto, setPendingPhoto] = useState<string | null>(nav.scanPhoto ?? null);
  const [recipeHasImage, setRecipeHasImage] = useState(false);
  const [imageSource, setImageSource] = useState<string | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await RecipesApi.get(id);
        if (!cancelled) {
          setForm(fromRecipe(r));
          setRecipeHasImage(Boolean(r.has_image));
          setImageSource(r.image_source ?? null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Couldn't load that recipe.");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const patch = useCallback((p: Partial<Form>) => setForm((f) => ({ ...f, ...p })), []);

  const patchIngredient = (key: string, p: Partial<EditIngredient>) =>
    setForm((f) => ({
      ...f,
      ingredients: f.ingredients.map((i) => (i.key === key ? { ...i, ...p } : i)),
    }));
  const patchStep = (key: string, p: Partial<EditStep>) =>
    setForm((f) => ({ ...f, steps: f.steps.map((s) => (s.key === key ? { ...s, ...p } : s)) }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setError(null);
    const body = {
      ...toBody(form, nav.source ?? "manual", nav.sourceUrl),
      // Only on create, and only from an import. The server fetches and stores
      // it, so an abandoned draft leaves no bytes behind.
      ...(isEdit || !nav.imageUrl ? {} : { imageUrl: nav.imageUrl }),
    };
    try {
      // PUT, not PATCH, when editing: ingredients and steps are child rows, and
      // a partial update would leave the old ones alongside the new.
      const saved = isEdit
        ? await RecipesApi.replace(id!, body)
        : await RecipesApi.create(body);

      // The picture, once there is an id to hang it on. Its own request rather
      // than part of the recipe body: 300 KB of base64 inside a JSON payload
      // would dwarf the recipe, and a failed upload here must not lose the
      // recipe that already saved.
      if (pendingPhoto) {
        await RecipesApi.setImage(saved.id, pendingPhoto, nav.scanPhoto ? "scan" : "upload")
          .catch(() => toast("The recipe saved, but the photo didn't upload.", "danger"));
      }

      toast(isEdit ? "Saved" : "Recipe added", "ok");
      navigate(`/recipes/${saved.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save.");
    }
    setSaving(false);
  };

  if (loading) return <Loading />;

  const lowConfidence = nav.lowConfidence ?? [];

  return (
    <div className="max-w-3xl animate-fade-in">
      <PageHeader
        title={isEdit ? "Edit recipe" : nav.draft ? "Check this over" : "Write a recipe"}
        subtitle={nav.draft
          ? "Nothing is saved until you press Save. Check the quantities."
          : undefined}
        actions={
          <>
            <button className="btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
            <button className="btn-primary" disabled={saving || !form.title.trim()}
                    onClick={() => void save()}>
              {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      />

      {lowConfidence.length > 0 && (
        <div className="mb-4">
          <Banner tone="warn">
            {lowConfidence.length} field{lowConfidence.length === 1 ? " was" : "s were"} hard
            to read: {lowConfidence.join(", ")}. Check them before saving.
          </Banner>
        </div>
      )}
      {error && <div className="mb-4"><ErrorBox message={error} /></div>}

      {/* ── Basics ───────────────────────────────────────────────────── */}
      <section className="card mb-4 space-y-3 p-5">
        <label className="block">
          <span className="text-sm font-medium">Title</span>
          <input className="input mt-1" value={form.title} autoFocus
                 onChange={(e) => patch({ title: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm">Description</span>
          <textarea className="input mt-1" rows={2} value={form.description}
                    onChange={(e) => patch({ description: e.target.value })} />
        </label>

        <RecipePhotoField
          recipeId={id}
          hasImage={recipeHasImage}
          imageSource={imageSource}
          value={pendingPhoto}
          onChange={setPendingPhoto}
          onRemoveExisting={() => {
            if (!id) return;
            void RecipesApi.clearImage(id)
              .then(() => { setRecipeHasImage(false); setImageSource(null); })
              .catch((e: Error) => toast(e.message, "danger"));
          }}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-sm">Servings</span>
            <input className="input mt-1" inputMode="decimal" value={form.servingsBase}
                   onChange={(e) => patch({ servingsBase: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-sm">Prep (min)</span>
            <input className="input mt-1" inputMode="numeric" value={form.prepMinutes}
                   onChange={(e) => patch({ prepMinutes: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-sm">Cook (min)</span>
            <input className="input mt-1" inputMode="numeric" value={form.cookMinutes}
                   onChange={(e) => patch({ cookMinutes: e.target.value })} />
          </label>
        </div>
      </section>

      {/* ── Ingredients ──────────────────────────────────────────────── */}
      <section className="card mb-4 p-5">
        <h2 className="mb-3 font-medium">Ingredients</h2>
        <ul className="space-y-2">
          {form.ingredients.map((ing) => (
            <li key={ing.key} className="rounded-lg border border-rule-soft p-2.5">
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="Ingredient"
                       value={ing.displayName}
                       onChange={(e) => patchIngredient(ing.key, { displayName: e.target.value })} />
                <button className="px-2 text-ink-3 hover:text-danger" title="Remove"
                        onClick={() => setForm((f) => ({
                          ...f, ingredients: f.ingredients.filter((x) => x.key !== ing.key),
                        }))}>✕</button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input className="input w-24" placeholder="Qty" inputMode="decimal"
                       value={ing.quantity}
                       onChange={(e) => patchIngredient(ing.key, { quantity: e.target.value })} />
                <input className="input w-24" placeholder="Unit" value={ing.unit}
                       onChange={(e) => patchIngredient(ing.key, { unit: e.target.value })} />
                <input className="input flex-1" placeholder="Prep note, e.g. finely chopped"
                       value={ing.prepNote}
                       onChange={(e) => patchIngredient(ing.key, { prepNote: e.target.value })} />
                {/* Salt is not linear — doubling a recipe and doubling the salt
                    makes it inedible — so the mode is exposed, not guessed. */}
                <select className="input w-36" value={ing.scaleMode}
                        title="How this scales when the recipe is resized"
                        onChange={(e) => patchIngredient(ing.key, {
                          scaleMode: e.target.value as ScaleMode,
                        })}>
                  <option value="linear">Scales</option>
                  <option value="sublinear">Scales gently</option>
                  <option value="fixed">Fixed</option>
                </select>
                <label className="flex items-center gap-1.5 text-xs text-ink-3">
                  <input type="checkbox" checked={ing.isOptional}
                         onChange={(e) => patchIngredient(ing.key, {
                           isOptional: e.target.checked,
                         })} />
                  optional
                </label>
              </div>
            </li>
          ))}
        </ul>
        <button className="btn-ghost mt-3 text-xs"
                onClick={() => setForm((f) => ({
                  ...f, ingredients: [...f.ingredients, blankIngredient()],
                }))}>
          + Add ingredient
        </button>
      </section>

      {/* ── Method ───────────────────────────────────────────────────── */}
      <section className="card mb-4 p-5">
        <h2 className="mb-3 font-medium">Method</h2>
        <ol className="space-y-2">
          {form.steps.map((step, i) => (
            <li key={step.key} className="flex items-start gap-2">
              <span className="mt-2.5 w-5 shrink-0 text-right text-sm font-medium text-accent">
                {i + 1}.
              </span>
              <div className="flex-1">
                <textarea className="input" rows={2} placeholder="What to do"
                          value={step.text}
                          onChange={(e) => patchStep(step.key, { text: e.target.value })} />
                <input className="input mt-1 w-32" placeholder="timer (min)" inputMode="numeric"
                       value={step.durationMinutes}
                       onChange={(e) => patchStep(step.key, {
                         durationMinutes: e.target.value,
                       })} />
              </div>
              <button className="mt-2 px-2 text-ink-3 hover:text-danger" title="Remove"
                      onClick={() => setForm((f) => ({
                        ...f, steps: f.steps.filter((x) => x.key !== step.key),
                      }))}>✕</button>
            </li>
          ))}
        </ol>
        <button className="btn-ghost mt-3 text-xs"
                onClick={() => setForm((f) => ({ ...f, steps: [...f.steps, blankStep()] }))}>
          + Add step
        </button>
      </section>

      {/* ── More ─────────────────────────────────────────────────────── */}
      <section className="card mb-6 space-y-3 p-5">
        <h2 className="font-medium">More</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm">Cuisine</span>
            <input className="input mt-1" value={form.cuisine}
                   onChange={(e) => patch({ cuisine: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-sm">Course</span>
            <input className="input mt-1" value={form.course}
                   onChange={(e) => patch({ course: e.target.value })} />
          </label>
        </div>
        <label className="block">
          <span className="text-sm">Diet tags, comma separated</span>
          <input className="input mt-1" value={form.dietTags}
                 onChange={(e) => patch({ dietTags: e.target.value })} />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-sm">Source</span>
            <select className="input mt-1" value={form.source}
                    onChange={(e) => {
                      const newSource = e.target.value;
                      patch({ source: newSource });
                      // Clear sourceName if source doesn't need it
                      if (newSource !== "cookbook" && newSource !== "family") {
                        patch({ sourceName: "" });
                      }
                    }}>
              <option value="manual">Typed manually</option>
              <option value="ai">AI Generated</option>
              <option value="ocr">Scanned (OCR)</option>
              <option value="url">Imported from URL</option>
              <option value="template">From template</option>
              <option value="fork">Duplicated from another</option>
              <option value="cookbook">From cookbook</option>
              <option value="family">From family</option>
            </select>
          </label>
          {(form.source === "cookbook" || form.source === "family") && (
            <label className="block">
              <span className="text-sm">
                {form.source === "cookbook" ? "Cookbook name" : "Family name"}
              </span>
              <input className="input mt-1" placeholder={form.source === "cookbook" ? "e.g., Joy of Cooking" : "e.g., Grandma Smith"}
                     value={form.sourceName}
                     onChange={(e) => patch({ sourceName: e.target.value })} />
            </label>
          )}
        </div>
        <label className="block">
          <span className="text-sm">Notes</span>
          <textarea className="input mt-1" rows={3} value={form.notes}
                    onChange={(e) => patch({ notes: e.target.value })} />
        </label>
      </section>

      <div className="flex justify-end gap-2 pb-8">
        <button className="btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
        <button className="btn-primary" disabled={saving || !form.title.trim()}
                onClick={() => void save()}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
